/**
 * PortfolioAnalyticsPanel — performance analytics for paper/live trading.
 * Tabs: Overview, By Symbol, Equity Curve.
 */

import { memo, useState, useMemo } from 'react';
import { useTerminalStore } from '../../stores/terminal.store.js';
import {
  calcTradeMetrics,
  calcMaxDrawdown,
  calcSymbolMetrics,
  calcOpenSummary,
} from '../../utils/portfolioStats.js';

// ─── Shared stat cell ─────────────────────────────────────────────────────────

function Stat({
  label, value, sub, color,
}: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div className="flex flex-col gap-0.5 border border-hud p-1.5 bg-black/20">
      <span className="font-mono text-[11px] tracking-widest opacity-90 uppercase">{label}</span>
      <span
        className="font-orb font-bold text-[12px]"
        style={{ color: color ?? 'var(--color-bright)' }}
      >
        {value}
      </span>
      {sub && <span className="font-mono text-[11px] opacity-35">{sub}</span>}
    </div>
  );
}

function PnlColor(val: number): string {
  return val > 0 ? 'var(--color-buy)' : val < 0 ? 'var(--color-sell)' : 'var(--color-muted)';
}

// ─── Equity sparkline (SVG) ───────────────────────────────────────────────────

function EquitySparkline({ curve }: { curve: { timestamp: number; equity: number }[] }) {
  if (curve.length < 2) {
    return (
      <div className="flex items-center justify-center h-full opacity-90 font-mono text-[11px]">
        NOT ENOUGH DATA
      </div>
    );
  }

  const W = 400; const H = 80;
  const xs = curve.map(p => p.timestamp);
  const ys = curve.map(p => p.equity);
  const minX = Math.min(...xs); const maxX = Math.max(...xs);
  const minY = Math.min(...ys); const maxY = Math.max(...ys);
  const rangeX = maxX - minX || 1; const rangeY = maxY - minY || 1;

  const pts = curve.map(p => {
    const x = ((p.timestamp - minX) / rangeX) * (W - 8) + 4;
    const y = H - ((p.equity - minY) / rangeY) * (H - 12) - 6;
    return `${x},${y}`;
  });

  const polyline = pts.join(' ');
  const areaPath = `M ${pts[0]} L ${pts.join(' L ')} L ${(W - 4)},${H} L 4,${H} Z`;
  const isPositive = ys[ys.length - 1]! >= ys[0]!;
  const color = isPositive ? '#00ff88' : '#ff3c3c';

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#sparkGrad)" />
      <polyline
        points={polyline}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      {/* Start and end dots */}
      {pts[0] && <circle cx={pts[0].split(',')[0]} cy={pts[0].split(',')[1]} r="2" fill={color} opacity="0.5" />}
      {pts[pts.length - 1] && (
        <circle cx={pts[pts.length - 1]!.split(',')[0]} cy={pts[pts.length - 1]!.split(',')[1]} r="2.5" fill={color} />
      )}
    </svg>
  );
}

// ─── Overview tab ─────────────────────────────────────────────────────────────

function OverviewTab() {
  const orders      = useTerminalStore(s => s.orders);
  const positions   = useTerminalStore(s => s.positions);
  const paperAcct   = useTerminalStore(s => s.paperAccount);
  const equityCurve = useTerminalStore(s => s.equityCurve);
  const riskSettings = useTerminalStore(s => s.riskSettings);
  const cbActive    = useTerminalStore(s => s.circuitBreakerActive);

  const metrics   = useMemo(() => calcTradeMetrics(orders), [orders]);
  const { drawdown, drawdownPct } = useMemo(() => calcMaxDrawdown(equityCurve), [equityCurve]);
  const openSum   = useMemo(() => calcOpenSummary(positions), [positions]);

  const pnlColor = PnlColor(metrics.totalPnl);

  return (
    <div className="p-2 flex flex-col gap-3">
      {/* Circuit breaker warning */}
      {cbActive && (
        <div className="border border-sell/50 bg-sell/10 px-2 py-1.5 flex items-center gap-2">
          <span className="text-sell text-[12px]">⚡</span>
          <div>
            <div className="font-orb font-bold text-[11px] text-sell tracking-widest">CIRCUIT BREAKER ACTIVE</div>
            <div className="font-mono text-[11px] opacity-90">
              Daily loss limit reached ({riskSettings.maxDailyLossPct}%)
            </div>
          </div>
        </div>
      )}

      {/* Account summary */}
      <div className="grid grid-cols-2 gap-1">
        <Stat label="Equity"
              value={`$${paperAcct.equity.toFixed(2)}`}
              sub={`Balance $${paperAcct.balance.toFixed(2)}`} />
        <Stat label="Total P&L"
              value={`${metrics.totalPnl >= 0 ? '+' : ''}$${metrics.totalPnl.toFixed(2)}`}
              color={pnlColor}
              sub={`${metrics.totalTrades} trades`} />
        <Stat label="Win Rate"
              value={`${metrics.winRate}%`}
              sub={`${metrics.winTrades}W / ${metrics.lossTrades}L`}
              color={metrics.winRate >= 50 ? 'var(--color-buy)' : 'var(--color-sell)'} />
        <Stat label="Profit Factor"
              value={
                metrics.profitFactor === Infinity ? '∞'
                : metrics.profitFactor > 0 ? metrics.profitFactor.toFixed(2) : '—'
              }
              sub="gross profit / gross loss"
              {...(metrics.profitFactor >= 1.5 ? { color: 'var(--color-buy)' } : {})} />
      </div>

      {/* Risk metrics */}
      <div className="grid grid-cols-3 gap-1">
        <Stat label="Avg Win"
              value={metrics.avgWin  > 0 ? `$${metrics.avgWin.toFixed(2)}`  : '—'}
              color="var(--color-buy)" />
        <Stat label="Avg Loss"
              value={metrics.avgLoss > 0 ? `$${metrics.avgLoss.toFixed(2)}` : '—'}
              color="var(--color-sell)" />
        <Stat label="Sharpe"
              value={metrics.sharpeRatio !== 0 ? metrics.sharpeRatio.toFixed(2) : '—'}
              {...(metrics.sharpeRatio >= 1 ? { color: 'var(--color-buy)' } : {})} />
        <Stat label="Max DD"
              value={drawdown > 0 ? `$${drawdown.toFixed(2)}` : '$0.00'}
              {...(drawdownPct > 10 ? { color: 'var(--color-sell)' } : {})} />
        <Stat label="DD %"
              value={drawdownPct > 0 ? `${drawdownPct.toFixed(1)}%` : '0%'}
              {...(drawdownPct > 10 ? { color: 'var(--color-sell)' } : {})} />
        <Stat label="Avg Hold"
              value={metrics.avgHoldingHrs > 0 ? `${metrics.avgHoldingHrs}h` : '—'} />
      </div>

      {/* Open positions summary */}
      {positions.length > 0 && (
        <div className="border border-hud p-2">
          <div className="font-mono text-[11px] tracking-widest opacity-90 mb-1.5">OPEN POSITIONS</div>
          <div className="grid grid-cols-3 gap-1">
            <Stat label="Open Pos" value={String(positions.length)}
                  sub={`${openSum.longCount}L / ${openSum.shortCount}S`} />
            <Stat label="Exposure"  value={`$${openSum.totalSize.toFixed(0)}`} />
            <Stat label="Unreal P&L" value={`${openSum.totalPnl >= 0 ? '+' : ''}$${openSum.totalPnl.toFixed(2)}`}
                  color={PnlColor(openSum.totalPnl)} />
          </div>
        </div>
      )}

      {/* Best / Worst */}
      {metrics.totalTrades > 0 && (
        <div className="grid grid-cols-2 gap-1">
          <Stat label="Best Trade"  value={`+$${metrics.bestTrade.toFixed(2)}`}  color="var(--color-buy)" />
          <Stat label="Worst Trade" value={`-$${Math.abs(metrics.worstTrade).toFixed(2)}`} color="var(--color-sell)" />
        </div>
      )}
    </div>
  );
}

// ─── By-symbol tab ────────────────────────────────────────────────────────────

function BySymbolTab() {
  const orders  = useTerminalStore(s => s.orders);
  const symRows = useMemo(() => calcSymbolMetrics(orders), [orders]);

  if (symRows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 opacity-30 p-4">
        <div className="text-[24px]">⬡</div>
        <span className="font-mono text-[11px] tracking-widest">NO TRADE HISTORY</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="grid grid-cols-5 gap-0 border-b border-hud px-2 py-1 flex-shrink-0"
           style={{ gridTemplateColumns: '3fr 2fr 2fr 2fr 2fr' }}>
        {['SYMBOL', 'PNL', 'WIN%', 'TRADES', 'PF'].map(h => (
          <div key={h} className="font-mono text-[11px] tracking-widest opacity-90">{h}</div>
        ))}
      </div>

      {symRows.map(row => (
        <div
          key={row.symbol}
          className="grid px-2 py-1.5 border-b border-hud/30 hover:bg-white/[0.02] transition-colors"
          style={{ gridTemplateColumns: '3fr 2fr 2fr 2fr 2fr' }}
        >
          <span className="font-orb font-bold text-[11px]" style={{ color: 'var(--color-bright)' }}>
            {row.symbol}
          </span>
          <span className="font-mono text-[11px]" style={{ color: PnlColor(row.totalPnl) }}>
            {row.totalPnl >= 0 ? '+' : ''}${row.totalPnl.toFixed(2)}
          </span>
          <span className="font-mono text-[11px]"
                style={{ color: row.winRate >= 50 ? 'var(--color-buy)' : 'var(--color-sell)' }}>
            {row.winRate}%
          </span>
          <span className="font-mono text-[11px] opacity-90">{row.totalTrades}</span>
          <span className="font-mono text-[11px]"
                style={{ color: row.profitFactor >= 1.5 ? 'var(--color-buy)' : 'var(--color-bright)' }}>
            {row.profitFactor === Infinity ? '∞'
              : row.profitFactor > 0 ? row.profitFactor.toFixed(2) : '—'}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Equity curve tab ─────────────────────────────────────────────────────────

function EquityCurveTab() {
  const equityCurve = useTerminalStore(s => s.equityCurve);
  const startEquity = equityCurve[0]?.equity ?? 10_000;
  const lastEquity  = equityCurve[equityCurve.length - 1]?.equity ?? startEquity;
  const change      = lastEquity - startEquity;
  const changePct   = (change / startEquity) * 100;

  return (
    <div className="flex flex-col gap-2 p-2">
      {/* Summary */}
      <div className="flex items-center justify-between border border-hud p-2 bg-black/20">
        <div>
          <div className="font-mono text-[11px] opacity-90 tracking-widest">EQUITY</div>
          <div className="font-orb font-bold text-[14px]" style={{ color: 'var(--color-bright)' }}>
            ${lastEquity.toFixed(2)}
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[11px] opacity-90 tracking-widest">RETURN</div>
          <div className="font-orb font-bold text-[13px]"
               style={{ color: PnlColor(change) }}>
            {change >= 0 ? '+' : ''}${change.toFixed(2)}
          </div>
          <div className="font-mono text-[11px]" style={{ color: PnlColor(change) }}>
            {changePct >= 0 ? '+' : ''}{changePct.toFixed(2)}%
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="border border-hud bg-black/30" style={{ height: '120px' }}>
        <EquitySparkline curve={equityCurve} />
      </div>

      {/* Data points count */}
      <div className="font-mono text-[11px] opacity-90 text-right">
        {equityCurve.length} data points
      </div>
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

type AnalyticsTab = 'overview' | 'by-symbol' | 'equity';

export const PortfolioAnalyticsPanel = memo(function PortfolioAnalyticsPanel() {
  const [tab, setTab] = useState<AnalyticsTab>('overview');

  const tabs: { id: AnalyticsTab; label: string }[] = [
    { id: 'overview',   label: 'OVERVIEW' },
    { id: 'by-symbol',  label: 'BY SYMBOL' },
    { id: 'equity',     label: 'EQUITY' },
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Tabs */}
      <div className="flex border-b border-hud flex-shrink-0">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 font-mono text-[11px] py-1.5 tracking-widest border-b-2 transition-all ${
              tab === t.id ? 'border-arc' : 'border-transparent hover:border-arc/50'
            }`}
            style={{ color: tab === t.id ? 'var(--color-arc)' : 'var(--color-bright)' }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {tab === 'overview'  && <OverviewTab />}
        {tab === 'by-symbol' && <BySymbolTab />}
        {tab === 'equity'    && <EquityCurveTab />}
      </div>
    </div>
  );
});
